/**
 * This module provides the form handler for the Wallbox-Settings of a Storage device.
 *
 * It uses a work in progress implementation of the Formik form handler.
 */

import React from 'react'
import { WallboxConfigurationModel } from 'src/Libs/wallboxes'
import SettingsFormContainer from 'src/Elements/SystemSettings/forms/SettingsFormContainer'
import { AuthFieldSet, CommonFieldSet, HysteresisFieldSet, NetworkFieldSet } from 'src/Libs/wallboxes/components/settings/tiles'
import { Grid } from '@mui/material'
import WallboxSettingsFormGridContainer from './WallboxSettingsFormGridContainer'
import WallboxSettingsFormGridItem from './WallboxSettingsFormGridItem'
import { isUndefined } from 'lodash'
import { supportsCableLockForWallbox, supportsLedIntensityForWallbox } from 'src/Libs/wallboxes/utils/wallbox-setting-software-support'

interface Props {
  model: WallboxConfigurationModel
  hasWritingAccess: boolean
  softwareVersion?: string
}

/**
 * Configured form component for a Wallbox of type MultiConnect (@see https://docs.e3dc.com/x/zgrIAw)
 * There are Multi Connect I and II, both with wallbox type 6 (there is no clear unique property to distinguish them).
 * The authentication is different for the two wallbox types:
 * - Multi Connect I: KeyLock
 * - Multi Connect II: RFID
 */
const WallboxSettingsMultiConnectForm: React.FC<Props> = ({ model, hasWritingAccess, softwareVersion }) => {
  const hasKeyLock = !isUndefined(model.keyLockMode)
  const hasRfid = !hasKeyLock && !isUndefined(model.rfidReaderEnabled)
  const hasCableLock = supportsCableLockForWallbox(softwareVersion)
  const hasLedIntensity = supportsLedIntensityForWallbox(softwareVersion)

  return (
    <SettingsFormContainer model={model}>
      <WallboxSettingsFormGridContainer>
        {softwareVersion !== undefined && (
          <WallboxSettingsFormGridItem>
            <CommonFieldSet model={model} disabled={!hasWritingAccess} hasCableLock={hasCableLock} hasLedIntensity={hasLedIntensity} />
          </WallboxSettingsFormGridItem>
        )}

        <WallboxSettingsFormGridItem>
          <HysteresisFieldSet model={model} disabled={!hasWritingAccess} />
        </WallboxSettingsFormGridItem>
        <Grid item xs={12}>
          <AuthFieldSet model={model} disabled={!hasWritingAccess} hasRfidMode={hasRfid} hasKeyLockMode={hasKeyLock} rfidReaderDisabled />
        </Grid>
        <Grid item xs={12}>
          <NetworkFieldSet model={model} disabled={!hasWritingAccess} />
        </Grid>
      </WallboxSettingsFormGridContainer>
    </SettingsFormContainer>
  )
}

export default WallboxSettingsMultiConnectForm
